import React, { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { mediaHeaders } from 'components/forms/uploader/utils/helpers';
import { pdfThumbnailUrl } from 'utils/helpers';
import { mediaReplacedCount, onMediaReplaced } from 'utils/mediaReplaced';

/** One box for the grid and the table, so the media server caches one size per PDF */
const THUMBNAIL_BOX = 300;

/** Thumbnails kept for the session; the grid remounts cells as it scrolls */
const CACHE_SIZE = 200;

/** Start loading a little before the cell scrolls into view */
const VIEWPORT_MARGIN = '200px';

/** After a failure that may pass, wait this long before each new try, then give up until the next mount */
export const RETRY_DELAYS = [5000, 30000, 120000];

type PdfRecord = {
  url?: string | URL | null,
  remote?: boolean | null,
  updatedAt?: unknown,
};

// Resolves to the thumbnail image, or null when the media server can't render
// one for this PDF. Those answers are kept, so a damaged PDF isn't asked for
// again on every scroll; a failure that may pass (auth, rate limit, server
// error, network, a page that isn't an image) is dropped and tried again.
// Blobs, not blob urls: each <img> makes and revokes its own url, so dropping
// an entry here can never break an image on screen.
const cache = new Map<string, Promise<Blob | null>>();

// Tries made again after a failure that may pass, per thumbnail url. Part of
// the cache key, so cells already on screen fetch again when it moves.
const retries = new Map<string, number>();
const retryTimers = new Set<string>();
const retryListeners = new Set<() => void>();

/**
 * Whether a failed response is worth asking for again later
 * @param {number} status HTTP status
 */
function isTransient(status: number) {
  return status >= 500 || status === 401 || status === 403 || status === 408 || status === 429;
}

/**
 * Ask for the thumbnail at `url` again after a while, unless a try is already
 * waiting or the tries are used up
 * @param {string} url thumbnail url
 */
function scheduleRetry(url: string) {
  const attempt = retries.get(url) || 0;
  if (retryTimers.has(url) || attempt >= RETRY_DELAYS.length) return;

  retryTimers.add(url);
  setTimeout(() => {
    retryTimers.delete(url);
    retries.set(url, attempt + 1);
    retryListeners.forEach((listener) => listener());
  }, RETRY_DELAYS[attempt]);
}

/**
 * Fetch (or reuse) the thumbnail at `url`. The API needs auth headers, so it
 * can't be an <img src>
 * @param {string} url thumbnail url
 * @param {string} key cache key: the url plus what has changed since
 * @param {boolean} revalidate the file was replaced, so the browser's copy may be stale
 */
function loadThumbnail(url: string, key: string, revalidate: boolean): Promise<Blob | null> {
  const cached = cache.get(key);
  if (cached) {
    // Most recently used goes to the back of the eviction queue.
    cache.delete(key);
    cache.set(key, cached);
    return cached;
  }

  const retry = () => {
    if (cache.get(key) === thumbnail) cache.delete(key);
    scheduleRetry(url);
    return null;
  };

  // A replaced PDF keeps its url, so only then revalidate rather than trust the HTTP cache.
  const thumbnail: Promise<Blob | null> = fetch(url, { headers: mediaHeaders(), ...(revalidate ? { cache: 'no-cache' as RequestCache } : {}) })
    .then((response) => {
      if (!response.ok) return isTransient(response.status) ? retry() : null;
      // An image is all the media server sends; anything else is a proxy or login page.
      if (!/^image\//i.test(response.headers.get('content-type') || '')) return retry();
      return response.blob();
    })
    .catch(retry);

  cache.set(key, thumbnail);
  while (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value);

  return thumbnail;
}

/**
 * Nearest ancestor that scrolls, which clips the element before the viewport does
 * @param {Element} element
 */
function scrollParent(element: Element): Element | null {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflow, overflowX, overflowY } = window.getComputedStyle(parent);
    if (/auto|scroll|overlay/.test(`${overflow} ${overflowX} ${overflowY}`)) return parent;
  }
  return null;
}

/**
 * True once the element has come near the visible part of its scroll
 * container, and from then on. Always false while `active` is false; always
 * true without IntersectionObserver
 * @param {RefObject<Element>} ref
 * @param {boolean} active false for cells that will never fetch anything
 */
function useSeen(ref: RefObject<Element>, active: boolean) {
  const [seen, setSeen] = useState(typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (seen || !active || !ref.current) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setSeen(true);
    }, { root: scrollParent(ref.current), rootMargin: VIEWPORT_MARGIN });
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [seen, active]);

  return active && seen;
}

/**
 * Re-render when a file is replaced or a failed thumbnail is due another try
 * @param {boolean} active false for records that have no thumbnail
 */
function useMediaChanges(active: boolean) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!active) return undefined;

    const update = () => setTick((tick) => tick + 1);
    const offReplaced = onMediaReplaced(update);
    retryListeners.add(update);

    return () => {
      offReplaced();
      retryListeners.delete(update);
    };
  }, [active]);
}

/**
 * Thumbnail url for a record, or null when it has none
 * @param {PdfRecord} record media record
 */
function thumbnailUrl(record: PdfRecord | null | undefined) {
  return pdfThumbnailUrl(record, THUMBNAIL_BOX, THUMBNAIL_BOX);
}

/**
 * Blob url of a PDF's first-page thumbnail, or null while it loads, before
 * `enabled`, or for a record that isn't a PDF on the site
 * @param {PdfRecord} record media record
 * @param {boolean} enabled false to hold off fetching, e.g. until on screen
 */
export function usePdfThumbnail(record: PdfRecord | null | undefined, enabled = true): string | null {
  const url = thumbnailUrl(record);
  useMediaChanges(!!url);

  const replaced = url ? mediaReplacedCount(String(record?.url)) : 0;
  const key = url && `${url}#${String(record?.updatedAt || '')}#${replaced}#${retries.get(url) || 0}`;
  const [loaded, setLoaded] = useState<{ key: string, src: string | null } | null>(null);
  // The blob url this cell still owns; a revoked one must never reach an <img>.
  const live = useRef<string | null>(null);

  useEffect(() => {
    if (!url || !enabled) return undefined;

    let mounted = true;
    let src: string | null = null;
    loadThumbnail(url, key, replaced > 0).then((blob) => {
      if (!mounted) return;
      src = blob && URL.createObjectURL(blob);
      live.current = src;
      setLoaded({ key, src });
    });

    return () => {
      mounted = false;
      if (src) URL.revokeObjectURL(src);
      if (live.current === src) live.current = null;
    };
  }, [key, enabled]);

  // Only the result for this record; a reused cell must not show the last file's page.
  return loaded && loaded.key === key && loaded.src === live.current ? loaded.src : null;
}

type Props = {
  record: PdfRecord & { title?: string | null },
  /** Shown while the thumbnail loads, and for anything without one */
  fallback: ReactNode,
  maxWidth?: string,
  maxHeight?: string,
};

/**
 * First page of a PDF stored on the site, or the fallback (its file icon).
 * Fetched only once the cell nears the viewport, since the table isn't virtualised
 * @param {Props} props
 */
const PdfThumbnail = ({ record, fallback, maxWidth = '100%', maxHeight = '100%' }: Props) => {
  const ref = useRef<HTMLSpanElement>(null);
  const seen = useSeen(ref, !!thumbnailUrl(record));
  const src = usePdfThumbnail(record, seen);

  return (
    <span ref={ref}>
      {src ? (
        <img
          src={src}
          alt={record.title || 'PDF first page'}
          style={{ maxWidth, maxHeight, objectFit: 'contain', boxShadow: '0 0 0 1px #d9d9d9' }}
        />
      ) : fallback}
    </span>
  );
};

export default PdfThumbnail;
